import React, { useEffect } from 'react';
import { View, Modal, StyleSheet, Platform, useWindowDimensions, Text } from 'react-native';

// Resolve createPortal at module scope so the first render of the overlay can portal immediately.
// We try multiple strategies to accommodate different bundlers (CJS/ESM) and platforms.
let __cmCreatePortal: ((node: any, container: any) => any) | null = null;
try {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const rd = require('react-dom');
  if (rd && rd.createPortal) __cmCreatePortal = rd.createPortal.bind(rd);
} catch (e) {
  // ignore
}
if (!__cmCreatePortal && typeof globalThis !== 'undefined') {
  try {
    const g: any = globalThis as any;
    const rd = g.ReactDOM || g.reactDom || g['react-dom'] || null;
    if (rd && rd.createPortal) __cmCreatePortal = rd.createPortal.bind(rd);
  } catch (e) {
    // ignore
  }
}

export interface FullscreenWrapperProps {
  visible: boolean;
  onClose?: () => void;
  children?: React.ReactNode;
  /** max width for the inner card on wide screens (web/desktop) */
  maxWidth?: number | string;
  contentStyle?: any;
}

const FullscreenWrapper: React.FC<FullscreenWrapperProps> = ({
  visible,
  onClose,
  children,
  maxWidth = 420,
  contentStyle,
}) => {
  // Common hooks (must not be conditional)
  const { width } = useWindowDimensions();
  const isWeb = Platform.OS === 'web';
  const isMobile = width <= 900;
  // Force a rerender once react-dom has been lazily loaded, so we can switch from inline to portal immediately
  const [, setPortalTick] = React.useState(0);
  useEffect(() => {
    if (!isWeb || __cmCreatePortal) return;
    let mounted = true;
    (async () => {
      try {
        const rd: any = await import('react-dom');
        if (rd && rd.createPortal) {
          __cmCreatePortal = rd.createPortal.bind(rd);
          if (mounted) setPortalTick((n) => n + 1);
        }
      } catch (e) { /* ignore */ }
    })();
    return () => { mounted = false; };
  }, [isWeb]);

  // inject CSS helpers (once on web)
  useEffect(() => {
    if (!isWeb) return;
    try {
      const g: any = (globalThis as any) || {};
      if (!g || !g.document) return;
      if (!g.document.getElementById('fullscreen-wrapper-scrollbar-style')) {
        const style = g.document.createElement('style');
        style.id = 'fullscreen-wrapper-scrollbar-style';
        style.innerHTML = `
          [data-fullscreen-wrapper] *::-webkit-scrollbar { display: none !important; }
          [data-fullscreen-wrapper] * { scrollbar-width: none !important; -ms-overflow-style: none !important; }
        `;
        g.document.head.appendChild(style);
      }
      if (!g.document.getElementById('fullscreen-wrapper-overlay-style')) {
        const style2 = g.document.createElement('style');
        style2.id = 'fullscreen-wrapper-overlay-style';
        style2.innerHTML = `
          [data-fullscreen-overlay] { position: fixed !important; inset: 0 !important; z-index: 2147483640 !important; }
        `;
        g.document.head.appendChild(style2);
      }
    } catch (e) {}
  }, [isWeb]);

  // Lock scrolling while visible (web only). Do NOT hide headers/footers.
  useEffect(() => {
    if (!isWeb || !visible) return;
    const g: any = (globalThis as any) || {};
    const html = g?.document?.documentElement as HTMLElement | null;
    const body = g?.document?.body as HTMLElement | null;
    if (!html || !body) return;
    try {
      const prevHtml = html.style.overflow || '';
      const prevBody = body.style.overflow || '';
      const prevCount = Number(body.dataset.scrollLockCount || '0');
      body.dataset.scrollLockCount = String(prevCount + 1);
      if (prevCount === 0) {
        body.dataset.scrollLockPrev = prevBody;
        html.dataset.scrollLockPrev = prevHtml;
        html.style.overflow = 'hidden';
        body.style.overflow = 'hidden';
      }
      const forceUnlock = () => {
        try {
          body.dataset.scrollLockCount = '0';
          const restoreHtml = html.dataset.scrollLockPrev || 'visible';
          const restoreBody = body.dataset.scrollLockPrev || 'visible';
          html.style.overflow = restoreHtml;
          body.style.overflow = restoreBody;
          delete html.dataset.scrollLockPrev;
          delete (body as any).dataset.scrollLockPrev;
        } catch (e) {}
      };

      // Ensure unlock on abrupt nav/pagehide
      try { window.addEventListener('pagehide', forceUnlock, { once: true } as any); } catch (e) {}
      try { window.addEventListener('beforeunload', forceUnlock, { once: true } as any); } catch (e) {}

      return () => {
        try { window.removeEventListener('pagehide', forceUnlock as any); } catch (e) {}
        try { window.removeEventListener('beforeunload', forceUnlock as any); } catch (e) {}
        const currentCount = Number(body.dataset.scrollLockCount || '0');
        const nextCount = Math.max(currentCount - 1, 0);
        body.dataset.scrollLockCount = String(nextCount);
        if (nextCount === 0) {
          const restoreHtml = html.dataset.scrollLockPrev || 'visible';
          const restoreBody = body.dataset.scrollLockPrev || 'visible';
          html.style.overflow = restoreHtml;
          body.style.overflow = restoreBody;
          delete html.dataset.scrollLockPrev;
          delete (body as any).dataset.scrollLockPrev;
        }
      };
    } catch (e) {
      // fallback: best effort
      html.style.overflow = 'hidden';
      body.style.overflow = 'hidden';
      return () => {
        html.style.overflow = '';
        body.style.overflow = '';
      };
    }
  }, [isWeb, visible]);

  const safeChildren = (node: React.ReactNode) => {
    if (node === null || node === undefined) return null;
    if (typeof node === 'string' || typeof node === 'number') return <Text>{String(node)}</Text>;
    if (Array.isArray(node)) return node.map((c, i) => (typeof c === 'string' || typeof c === 'number' ? <Text key={i}>{String(c)}</Text> : (c as any)));
    return node as any;
  };

  // Rendering branches
  if (isWeb && visible) {
    const overlay = (
      <View
        style={[
          styles.webOverlay,
          isMobile ? styles.webOverlayMobile : null,
          { position: 'fixed' as any, zIndex: 2147483640 as any, inset: 0 as any, pointerEvents: 'auto' as any }
        ]}
        data-fullscreen-wrapper={1 as any}
        data-fullscreen-overlay={1 as any}
      >
        <View style={[styles.webContent, { maxWidth }, isMobile ? styles.webContentMobile : null, contentStyle]}>
          {safeChildren(children)}
        </View>
      </View>
    );
    const g: any = (globalThis as any) || {};
    if (g && typeof g.document !== 'undefined' && __cmCreatePortal) {
      try { return __cmCreatePortal(overlay, g.document.body); } catch (e) { /* fallthrough */ }
    }
    return overlay;
  }
  if (Platform.OS !== 'web') {
    // Native: use Modal to preserve platform-native behavior (slide animation, back handling)
    return (
      <Modal visible={visible} animationType="slide" transparent={false} onRequestClose={onClose}>
        <View style={styles.nativeContainer}>{children}</View>
      </Modal>
    );
  }
  return null;
};

const styles = StyleSheet.create({
  webOverlay: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.35)',
    alignItems: 'center',
    justifyContent: 'flex-start',
    paddingTop: 20,
    zIndex: 99999,
  },
  webOverlayMobile: {
    paddingTop: 0,
    justifyContent: 'flex-start',
    alignItems: 'stretch',
  },
  webContent: {
    width: '100%',
    backgroundColor: '#fff',
    borderRadius: 14,
    overflow: 'hidden',
    boxSizing: 'border-box' as any,
    maxHeight: '100%',
    flex: 1,
    // try to hide native scrollbars on web while keeping scrolling functional
    ...( { scrollbarWidth: 'none', msOverflowStyle: 'none' } as any ),
  },
  webContentMobile: {
    width: '100%',
    maxWidth: '100%',
    // make mobile content fill the overlay and be the scrolling surface
    position: 'absolute' as any,
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    height: '100%',
    borderRadius: 0,
    flex: 1,
    overflow: 'auto' as any,
  },
  nativeContainer: { flex: 1, backgroundColor: '#fff' },
});

export default FullscreenWrapper;
